/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { AccordionFeatureCardFragment } from "@cosmos/hygraph-schema";
import { useEffect, useState } from "react";
import { useLocation } from "@epic-mw/app-context";
import { Accordion, CosmosButtonProps, CosmosTitle, EdsIcon, FeaturePanel } from "@components/elements";
import { css, cva } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

const accordionItemStyles = cva({
	base: {
		width: "100%",
		backgroundColor: "eds.palette.transparent.light.005",
		borderRadius: "eds.dimension.40",
		border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
		paddingBlock: "eds.dimension.20",
		paddingInline: "eds.dimension.16",
		outlineOffset: "eds.dimension.2",
		"&:has(:focus-visible)": { outline: "token(spacing.eds.dimension.2) solid #fff" },
		"& .accordion-trigger": {
			cursor: "pointer",
			marginBlock: "-eds.dimension.24",
			marginInline: "-eds.dimension.16",
			outline: "none",
			borderRadius: "eds.dimension.40",
			paddingBlock: "eds.dimension.20",
			paddingInline: "eds.dimension.16",
			textAlign: "start",
			width: "calc(100% + (2 * 16px))",
			"&:has(:focus-visible), &:is(:focus-visible,:hover)": {
				"& .accordion-trigger-icon": {
					backgroundColor: "eds.fill.secondary.hover",
				},
			},
		},
		"& .accordion-trigger-icon": {
			transition: "transform token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)",
			flexShrink: 0,
			height: "eds.dimension.32!",
			width: "eds.dimension.32!",
			padding: "eds.dimension.8",
			borderRadius: "eds.borderRadius.full",
			background: "var(--fill-secondary-default, rgba(255, 255, 255, 0.15))",
		},
		"& .accordion-content": {
			overflow: "hidden",
			"&[data-state='open']": {
				animation: `radixAccordionSlideDown token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)`,
			},
			"&[data-state='closed']": {
				animation: `radixAccordionSlideUp token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)`,
			},
		},
	},
	variants: {
		expanded: {
			true: {
				"& .accordion-trigger-icon": {
					transform: "rotate(180deg)",
				},
			},
		},
	},
});

const featurePanelStyles = css({
	"&&": {
		backgroundColor: "eds.palette.transparent.light.005",
		border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
		lgDown: { flexDir: "row" },
		mdDown: { flexDir: "column" },
		"& .image": {
			"&.flip": { md: { order: 1 } },
			maxWidth: "459px",
			lgDown: { maxWidth: "unset", flexBasis: "1/2" },
		},
		"& .content": {
			padding: "eds.dimension.16",
			mdDown: { padding: "0" },
			"& .title-desc-links-column": {
				gap: "eds.dimension.32",
				height: "100%",
				smDown: {
					gap: "eds.dimension.16",
				},
				"& .title-desc-stack": {
					gap: "eds.dimension.8",
					"& .cosmos-text.heading, & .cosmos-rich-text.heading": {
						textStyle: "headingMD",
					},
					"& .cosmos-rich-text.paragraph": {
						textStyle: "paragraphMD",
					},
					smDown: {
						"& .cosmos-text.heading, & .cosmos-rich-text.heading": {
							textStyle: "headingSM",
						},
						"& .cosmos-rich-text.paragraph": {
							textStyle: "paragraphSM",
						},
					},
				},
				"& .link-list": {
					"& .cosmos-text.paragraph": {
						textStyle: "paragraphMD",
					},
				},
				"& .button-list svg": {
					height: "eds.dimension.20!",
					width: "eds.dimension.20!",
				},
			},
		},
	},
});

export interface AccordionFeatureCardProps extends AccordionFeatureCardFragment {
	index?: number;
}

export const AccordionFeatureCard = ({
	index,
	title,
	cards,
	defaultOpen,
	anchorLinkId,
}: AccordionFeatureCardProps): React.JSX.Element => {
	const itemId = `feature-${index}`;
	const location = useLocation();

	const defaultValue: string[] = [];
	if (defaultOpen && itemId) defaultValue.push(itemId);
	const [value, setValue] = useState<string[]>(defaultValue);

	const handleChange = (val: string[]): void => setValue(val);

	useEffect(() => {
		const hash = location.hash.slice(1);
		if (hash) {
			if (hash === anchorLinkId) {
				setValue([itemId]);
				document.getElementById(anchorLinkId)?.scrollIntoView({ behavior: "smooth", block: "start" });
			} else if (defaultOpen && hash === itemId) {
				setValue([itemId]);
			}
		}
	}, [location.hash, anchorLinkId, itemId, defaultOpen]);

	return (
		<VStack id={anchorLinkId || itemId} alignItems="stretch" gap="eds.dimension.16">
			<Accordion defaultValue={defaultValue} value={value} onValueChange={handleChange}>
				<VStack alignItems="start">
					<Accordion.Item className={accordionItemStyles({ expanded: value.includes(itemId) })} value={itemId}>
						<Accordion.Trigger className="accordion-trigger">
							<HStack
								justifyContent="space-between"
								paddingInlineStart="eds.dimension.16"
								paddingInlineEnd="eds.dimension.4"
								gap="eds.dimension.8"
							>
								<CosmosTitle
									className={css({ textStyle: "headingMD", smDown: { textStyle: "headingSM" } })}
									title={{ text: title?.text, as: "h3" }}
								/>
								<EdsIcon className="accordion-trigger-icon" icon="ChevronDown" color="primary" />
							</HStack>
						</Accordion.Trigger>
						<Accordion.Content className="accordion-content">
							<div
								className={css({
									paddingBlockStart: "eds.dimension.16",
									display: "flex",
									flexDirection: "column",
									rowGap: "eds.dimension.16",
								})}
							>
								{cards.map((card, i) => (
									<FeaturePanel
										key={card.title?.text || `gamedev-feature-card-${i}`}
										className={featurePanelStyles}
										linksAsTags={false}
										{...card}
										buttons={card?.buttons?.map(
											(button): CosmosButtonProps => ({ ...button, icon: "ArrowRightCircle", iconPosition: "end" }),
										)}
									/>
								))}
							</div>
						</Accordion.Content>
					</Accordion.Item>
				</VStack>
			</Accordion>
		</VStack>
	);
};
